import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as St from './SavedEstimateList.styles';

import StoresList from '~/UI/components/organisms/StoresList';
import { useStores } from '~/api/easy-estimator-api/stores-api/useStores';
import { STATUS } from '~/core/constants';
import useEstimateSaver from '~/core/hooks/useEstimateSaver';
import useExpiredEstimateActions from '~/core/hooks/useExpiredEstimateActions';
import { StoreModel } from '~/core/models';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectCurrentEstimateCode,
  selectEstimateHasBeenSaved,
  updateEstimateHasBeenSaved,
} from '~/redux/slices/estimate';
import InformationModal from '~molecules/InformationModal';
import InputOption from '~molecules/InputOption';

const SavedEstimateList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isEstimateDirty } = useEstimateSaver();
  const {
    fetchedEstimate,
    startNewEstimate,
    switchLoadedEstimateCode,
    isFetchingEstimate,
  } = useEstimateContext();

  const { stores } = useStores();

  const { createCloneEstimate } = useExpiredEstimateActions();

  const currentEstimateNumber = useAppSelector(selectCurrentEstimateCode);
  const estimateHasBeenSaved = useAppSelector(selectEstimateHasBeenSaved);
  const [showCompletedModal, setShowCompletedModal] =
    useState(estimateHasBeenSaved);
  const [useCurrentDesign, setUseCurrentDesign] = useState(true);
  const isExpiredOrClosed = useMemo(
    () =>
      fetchedEstimate?.status === STATUS.EXPIRED ||
      fetchedEstimate?.status === STATUS.CLOSED,
    [fetchedEstimate?.status]
  );
  const isLeadEstimate = useMemo(
    () => fetchedEstimate?.estimateNumber.startsWith('L-'),
    [fetchedEstimate?.estimateNumber]
  );
  const isUserEstimate = useMemo(
    () => fetchedEstimate?.estimateNumber.startsWith('EST-'),
    [fetchedEstimate?.estimateNumber]
  );
  const canStartNewEstimate = useMemo(
    () => isUserEstimate || isLeadEstimate || isExpiredOrClosed,
    [isUserEstimate, isLeadEstimate, isExpiredOrClosed]
  );

  const menuItems = useMemo(() => {
    if (!fetchedEstimate) {
      return [];
    }

    const estimatedRevisions = (fetchedEstimate.revisions ?? []).filter(
      (rev) => rev.code !== fetchedEstimate.code
    );
    const allRevisions = [fetchedEstimate, ...estimatedRevisions];

    return allRevisions.map((version) => ({
      label:
        isEstimateDirty && version.estimateNumber === currentEstimateNumber
          ? `${version.estimateNumber} - Revised, please save`
          : version.estimateNumber,
      value: version.code,
      key: version.code,
    }));
  }, [fetchedEstimate, isEstimateDirty, currentEstimateNumber]);

  const currentSelectedValue = useMemo(() => {
    if (!fetchedEstimate) {
      return undefined;
    }

    // currentEstimateNumber now contains the GUID, use it directly
    return currentEstimateNumber ?? fetchedEstimate.code;
  }, [fetchedEstimate, currentEstimateNumber]);

  const fetchedEstimateAddress = useMemo(() => {
    if (!fetchedEstimate) {
      return '';
    }

    const {
      customerAddress1,
      customerAddress2,
      customerCity,
      customerState,
      customerZip,
    } = fetchedEstimate;

    return `${customerAddress1 || customerAddress2}, ${customerCity}, ${customerState}, ${customerZip}`;
  }, [fetchedEstimate]);

  const fetchedEstimateStore: StoreModel | undefined = useMemo(() => {
    if (!fetchedEstimate || !stores) {
      return undefined;
    }

    return stores.find(
      (store) => store.companyId === fetchedEstimate.estimateData?.storeId
    );
  }, [fetchedEstimate, stores]);

  const handleCloseModal = () => {
    dispatch(updateEstimateHasBeenSaved(false));
    setShowCompletedModal(false);
  };

  const handleRevisionChange = (code: string) => {
    switchLoadedEstimateCode(code);
  };

  const handleStartNewEstimate = useCallback(() => {
    if (canStartNewEstimate && useCurrentDesign) {
      // If checkbox is checked and estimate is expired/closed, behave like "Copy Estimate & Update Pricing"
      const translationSection =
        fetchedEstimate?.status === STATUS.EXPIRED ? 'EXPIRED' : 'CLOSED';

      createCloneEstimate(translationSection);
    } else {
      // Otherwise, start a new estimate
      startNewEstimate();
    }
  }, [
    canStartNewEstimate,
    useCurrentDesign,
    fetchedEstimate?.status,
    createCloneEstimate,
    startNewEstimate,
  ]);

  return (
    <>
      {fetchedEstimate && (
        <St.Container>
          <St.Title>{t('ESTIMATE_LIST.TITLE')}</St.Title>
          <St.ContentWrapper>
            <St.RowsContainer>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.ESTIMATE_NUMBER')}:</St.RowTitle>
                <St.SelectList
                  options={menuItems}
                  value={currentSelectedValue}
                  style={{ minWidth: 150 }}
                  onChange={handleRevisionChange}
                  loading={isFetchingEstimate}
                />
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.CUSTOMER_NAME')}:</St.RowTitle>{' '}
                {fetchedEstimate.customerName}
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.EMAIL')}:</St.RowTitle>{' '}
                {fetchedEstimate.customerEmail}
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.ADDRESS')}:</St.RowTitle>{' '}
                {fetchedEstimateAddress}
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.SELECTED_STORE')}:</St.RowTitle>{' '}
                {fetchedEstimateStore
                  ? `${fetchedEstimateStore?.companyName}, ${fetchedEstimateStore?.address}`
                  : ''}
              </St.Row>
              <St.Row>
                <St.RowTitle>
                  {t('ESTIMATE_LIST.SELECTED_MATERIAL')}:
                </St.RowTitle>{' '}
                {fetchedEstimate.estimateData?.materialDesc ?? ''}
              </St.Row>
              <St.Row>
                <St.RowTitle>{t('ESTIMATE_LIST.SELECTED_COLOR')}:</St.RowTitle>{' '}
                {fetchedEstimate.estimateData?.color?.name ?? ''}
              </St.Row>
            </St.RowsContainer>
            <St.ButtonContainer>
              <St.SaveEstimateButton onClick={handleStartNewEstimate}>
                {t('ESTIMATE_LIST.CREATE')}
              </St.SaveEstimateButton>
              {canStartNewEstimate && (
                <St.CheckboxContainer>
                  <InputOption
                    type="checkbox"
                    value="useCurrentDesign"
                    isChecked={useCurrentDesign}
                    onChange={(e) => setUseCurrentDesign(e.target.checked)}
                    label={t(
                      'SCROLLABLE_AREA.ADDRESS_INFORMATION.NEW_ESTIMATE.CHECKBOX'
                    )}
                  />
                </St.CheckboxContainer>
              )}
            </St.ButtonContainer>
          </St.ContentWrapper>
          <St.StoresListContainer>
            <StoresList
              showStoresList={isLeadEstimate}
              showZipCodeInput={false}
            />
          </St.StoresListContainer>
        </St.Container>
      )}
      <InformationModal
        title={t('ESTIMATE_LIST.MODAL_TITLE')}
        content={t('ESTIMATE_LIST.MODAL_DESCRIPTION')}
        isVisible={showCompletedModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default SavedEstimateList;
